import { FC, useCallback } from "react";
import {
    PaymentForm,
    ProductOrderItem,
    TaskSaleAdditional,
} from "tm-shared/types";

import { useInfiniteScroll, useQueueInfo } from "@/hooks";
import { useAppDispatch } from "@/hooks/redux";
import { GeneralProductCard } from "@/pages/WorkPlan/Tasks/components/GeneralProductCard";
import { useCreateCheckout } from "@/pages/WorkPlan/Tasks/hooks";
import { AdvancedProduct } from "@/pages/WorkPlan/Tasks/SalePage/SalePage.types";
import { Loading } from "@/shared/feedback/Loading";
import { BaseButton } from "@/shared/ui";
import { setSaleProduct } from "@/store/slices/progress";

import {
    GoodsWrapper,
    MainContainer,
    StyledCartBtnContainer,
} from "./ProductsFormCatalog.styled";

interface ProductsFormCatalogProps {
    task: TaskSaleAdditional;
    inventory: Record<string, number>;
    products: AdvancedProduct[];
    isOnlyPlan: boolean;
    paymentForm?: PaymentForm;
    payloadProducts: Record<string, ProductOrderItem>;
    loading: boolean;
    ignoreRemainings: string[];
    pointId: string;
}

const BATCH_SIZE = 20;

export const ProductsFormCatalog: FC<ProductsFormCatalogProps> = ({
    inventory,
    pointId,
    task,
    products,
    isOnlyPlan,
    paymentForm,
    payloadProducts,
    ignoreRemainings,
    loading,
}) => {
    const dispatch = useAppDispatch();

    const { isRunning: isQueueRunning, requestQueue } = useQueueInfo();
    const hasPendingTaskSync = requestQueue.some((q) => q.id === task.id);

    const { visibleItems: visibleProducts, loadMore } = useInfiniteScroll(
        isOnlyPlan
            ? products.filter((p) => p.productPlan || p.additionalPlan)
            : products,
        BATCH_SIZE,
    );

    const handleInView = useCallback(() => {
        loadMore();
    }, [loadMore]);

    const taskId = task.id;

    const { handle: handleSendProductsToCheckout, isLoading: completeLoading } =
        useCreateCheckout({
            task,
            pointId,
            products: payloadProducts,
            shouldIgnoreRemainings: ignoreRemainings.length > 0,
        });

    const handleCheckoutClick = () => {
        if (!paymentForm) return;

        handleSendProductsToCheckout(paymentForm);
    };

    const handleProductCountChange = useCallback(
        (product: ProductOrderItem) => {
            dispatch(
                setSaleProduct({
                    taskId,
                    product,
                }),
            );
        },
        [dispatch, taskId],
    );

    const completeVisible = Object.values(payloadProducts).length > 0;

    return (
        <MainContainer id="catalog-container">
            {(loading || completeLoading) && <Loading />}
            <GoodsWrapper>
                {visibleProducts.map((item, index, filteredArray) => {
                    const isLast = index === filteredArray.length - 1;
                    const currentCount =
                        payloadProducts[item.barcode]?.count ?? 0;
                    const isProductRemainingIgnored = ignoreRemainings.includes(
                        item.barcode,
                    );

                    return (
                        <GeneralProductCard
                            key={item.barcode}
                            {...item}
                            itemsLeft={inventory[item.barcode]}
                            hasSelectedPaymentForm={!!paymentForm}
                            value={currentCount}
                            taskId={taskId}
                            pointId={pointId}
                            isLast={isLast}
                            onInView={handleInView}
                            productPlan={item.productPlan}
                            additionalPlan={item.additionalPlan}
                            onCountChange={handleProductCountChange}
                            isRemainingIgnored={ignoreRemainings.length > 0}
                            isProductRemainingIgnored={
                                isProductRemainingIgnored
                            }
                        />
                    );
                })}
            </GoodsWrapper>

            {completeVisible && (
                <StyledCartBtnContainer>
                    <BaseButton
                        type="primary"
                        loading={completeLoading}
                        onClick={handleCheckoutClick}
                        disabled={hasPendingTaskSync && isQueueRunning}
                    >
                        Кошик
                    </BaseButton>
                </StyledCartBtnContainer>
            )}
        </MainContainer>
    );
};
